import { HttpClient, HttpParams, httpResource } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { FileReference } from '@core/file/models/file-reference.interface';
import { EnvironmentLoaderService } from 'ok-angular-shared/services';
import { toSnakeCase } from 'ok-angular-shared/utils';
import { map, Observable } from 'rxjs';
import { FileType } from 'src/app/installation/services/installation/filetype';
import { ScreeningExpandedStatusEnum } from 'src/app/screening/models/screening-expanded-status.enum';
import { mapScreeningExpandedStatusEnum } from 'src/app/screening/util/screening-expanded-status-enum-mapper';
import { IdTagNumberOrderRequestDto } from 'src/app/subscription/model';
import { AppSettings } from 'src/settings/appsettings.interface';
import { UpdateInstallationWithExtraCostsRequest } from '../../installation-add-extra-costs-dialog/models/update-installation-with-extra-costs-request.interface';
import {
  UpdateInstallationRequestDto,
  UpdateInstallationWithInvoiceRequestDto,
} from '../../installation-edit/models/update-installation-with-invoice-request-dto';
import { ChangeChargingStationDto, InstallationPaginatedDto, InstallationSearchEvent } from '../../model';
import { CompleteInstallationInformation } from '../models/complete-installation-information.interface';
import { CompleteInstallationRequestDto } from '../models/complete-installation-request-dto.interface';
import { CompleteRelocationRequestDto } from '../models/complete-relocation-request-dto.interface';
import { InstallationQueryParamsEnum } from '../models/installation-query-params.enum';
import { InstallationWithAddressDto } from '../models/installation-with-address.interface';
import { ScreeningDetailsByInstallationId } from '../models/screening-details-by-installation-id.interface';
import { UpdateDeliveryAddressRequestDto } from '../models/update-delivery-address-request-dto.interface';
import { ChargingStationAndReplacementEligibilityDto } from './charging-station-and-replacement-eligibility-dto';
import { DeleteInstallationFileResponseDto } from './delete-installation-file-responseDto';
import { UploadInstallationFileResponseDto } from './upload-installation-file-responseDto';

@Injectable({
  providedIn: 'root',
})
export class InstallationService {
  private http = inject(HttpClient);
  private environmentLoaderService = inject<EnvironmentLoaderService<AppSettings>>(EnvironmentLoaderService);

  private apiURL: string;
  private apiURLPlural: string;
  private fileApiUrl: string;

  public installationId = signal<number | undefined>(undefined);
  public screeningDetailsResource = httpResource<ScreeningDetailsByInstallationId>(
    () => {
      const id = this.installationId();
      return id ? `${this.apiURLPlural}/${id}/screening-details` : undefined;
    },
    {
      parse: (value: unknown) => {
        const dto = value as ScreeningDetailsByInstallationId;
        return {
          ...dto,
          status: mapScreeningExpandedStatusEnum(dto.status),
        };
      },
    }
  );

  constructor() {
    this.apiURL = this.environmentLoaderService.config.subscriptionServiceBaseUrl + '/api/v1/Installation';
    this.apiURLPlural = this.environmentLoaderService.config.subscriptionServiceBaseUrl + '/api/v1/installations';
    this.fileApiUrl = this.environmentLoaderService.config.subscriptionServiceBaseUrl + '/api/v1/file';
  }

  /**
   * Sets the active installation id whose screening details should be loaded.
   * Triggers the resource to (re)load data.
   */
  public setInstallationIdForScreeningDetails(installationId: number | undefined) {
    this.installationId.set(installationId);
  }

  public uploadFile(file: FileReference, installationId: string, fileType: FileType): Observable<UploadInstallationFileResponseDto> {
    const url = `${this.fileApiUrl}?installationId=${installationId}&fileType=${fileType}`;
    const formdata = new FormData();

    formdata.append('file', file.fileContent);

    return this.http.post<UploadInstallationFileResponseDto>(url, formdata).pipe(
      map(f => ({
        ...f,
        downloadUrl:
          this.environmentLoaderService.config.subscriptionServiceBaseUrl +
          `/api/v1/file/downloadInstallationFile?installationId=${installationId}&fileName=${f.fileName}`,
      }))
    );
  }

  public deleteFile(fileReference: FileReference, installationId: string): Observable<DeleteInstallationFileResponseDto> {
    const params: HttpParams = new HttpParams().append('installationId', installationId).append('fileName', fileReference.fileName);

    return this.http.delete<DeleteInstallationFileResponseDto>(this.fileApiUrl, { params });
  }

  public getInstallations(installationSearchEvent?: InstallationSearchEvent): Observable<InstallationPaginatedDto> {
    const url = this.apiURL;

    let parameters = new HttpParams();

    if (installationSearchEvent?.searchQuery) {
      parameters = parameters.append('q', installationSearchEvent?.searchQuery);
    }

    if (installationSearchEvent?.status) {
      parameters = parameters.append(InstallationQueryParamsEnum.status, installationSearchEvent?.status?.toString());
    }

    if (installationSearchEvent?.type) {
      parameters = parameters.append(InstallationQueryParamsEnum.type, installationSearchEvent?.type?.toString());
    }

    if (installationSearchEvent?.productChargingStationType) {
      parameters = parameters.append(
        InstallationQueryParamsEnum.productChargingStationType,
        installationSearchEvent?.productChargingStationType?.toString()
      );
    }

    if (installationSearchEvent?.customerType) {
      parameters = parameters.append(InstallationQueryParamsEnum.customerType, installationSearchEvent?.customerType?.toString());
    }

    if (installationSearchEvent?.includeDismantled) {
      parameters = parameters.append(InstallationQueryParamsEnum.includeDismantled, installationSearchEvent?.includeDismantled);
    }

    if (installationSearchEvent.pageIndex) {
      parameters = parameters.append(InstallationQueryParamsEnum.page, installationSearchEvent.pageIndex);
    }

    if (installationSearchEvent.pageSize) {
      parameters = parameters.append(InstallationQueryParamsEnum.pageSize, installationSearchEvent.pageSize);
    }

    if (installationSearchEvent.sort?.active) {
      parameters = parameters.append(InstallationQueryParamsEnum.sortBy, installationSearchEvent.sort.active);
      parameters = parameters.append(InstallationQueryParamsEnum.sortDirection, installationSearchEvent.sort.direction);
    }

    const options = {
      params: parameters,
    };

    return this.http.get<InstallationPaginatedDto>(url, options).pipe(map(response => this.mapGetInstallationsResponseDto(response)));
  }

  public getInstallationWithAddress(id: number): Observable<InstallationWithAddressDto> {
    const url = this.apiURLPlural + '/' + id + '/installation-with-address';
    return this.http.get<InstallationWithAddressDto>(url);
  }

  public getCompleteInstallationInformation(installationNumber: number): Observable<CompleteInstallationInformation> {
    const url = this.apiURLPlural + '/' + installationNumber + '/complete-installation-information';

    return this.http.get<CompleteInstallationInformation>(url);
  }

  public changeChargingStation(installationId: number, request: ChangeChargingStationDto) {
    const url = this.apiURL + '/' + installationId + '/changeChargingStation';

    return this.http.put<ChangeChargingStationDto>(url, request);
  }

  public completeInstallation(id: number, dto: CompleteInstallationRequestDto) {
    const url = `${this.apiURLPlural}/${id}/complete`;
    return this.http.post(url, toSnakeCase(dto));
  }

  public updateDeliveryAddress(id: number, dto: UpdateDeliveryAddressRequestDto) {
    const url = `${this.apiURLPlural}/${id}/update-delivery-address`;
    return this.http.post(url, toSnakeCase(dto));
  }

  public relocateInstallation(id: number, dto: CompleteRelocationRequestDto) {
    const url = this.apiURL + `/${id}/completeRelocation`;
    return this.http.put(url, dto);
  }

  public resetInstallationToStatusAwaiting(subscriptionId: number, installationId: number, chargingStationIdentifier: string) {
    const url =
      this.apiURL + '/portal' + `/${subscriptionId}` + `/${installationId}` + `/${chargingStationIdentifier}` + '/ResetToStatusAwaiting';
    return this.http.put<void>(url, null);
  }

  public dismantleInstallation(installationId: number, chargingStationIdentifier: string) {
    const url = this.apiURL + '/portal' + `/${installationId}` + `/${chargingStationIdentifier}` + '/DismantleInstallation';

    return this.http.put<void>(url, null);
  }

  public sendAddIdTagNumberOrderRequest(request: IdTagNumberOrderRequestDto) {
    const url = this.apiURL + '/AddIdTagToExistingGroupRequest';
    return this.http.post<void>(url, request);
  }

  public tryGetScreeningStatusByInstallationNumber(installationNumber: number): Observable<ScreeningExpandedStatusEnum> {
    const url = this.apiURL + `/${installationNumber}` + '/TryGetScreeningStatus';
    return this.http.get<ScreeningExpandedStatusEnum>(url).pipe(map(status => mapScreeningExpandedStatusEnum(status)));
  }

  public updateInstallation(id: number, dto: UpdateInstallationRequestDto) {
    const url = this.apiURL + `/${id}`;

    return this.http.put<void>(url, dto);
  }

  public updateInstallationWithExtraCosts(id: number, dto: UpdateInstallationWithExtraCostsRequest) {
    const url = this.apiURLPlural + `/${id}/extra-costs`;

    return this.http.put<void>(url, toSnakeCase(dto));
  }

  public updateWithInvoiceInformation(installationId: number, dto: UpdateInstallationWithInvoiceRequestDto) {
    const url = this.apiURLPlural + `/${installationId}` + '/invoice-information';

    return this.http.put<unknown>(url, toSnakeCase(dto));
  }

  public getChargingStationAndReplacementEligibilityForInstallation(
    id: number,
    subscriptionId: number
  ): Observable<ChargingStationAndReplacementEligibilityDto> {
    const url = this.apiURL + '/' + id + '/' + subscriptionId + '/ChargingStationAndReplacementEligibility';

    return this.http.get<ChargingStationAndReplacementEligibilityDto>(url);
  }

  private mapGetInstallationsResponseDto(response: InstallationPaginatedDto): InstallationPaginatedDto {
    response.items?.map(item => {
      item.screeningExpandedStatus = mapScreeningExpandedStatusEnum(item.screeningExpandedStatus);
    });
    return response;
  }
}
